import { Link, useNavigate } from "react-router-dom";
import { getConsultationPath } from "../../utils/consultationLink";
import { WEB_LEAD_MODULES } from "../../constants/webLeadModules";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import "./CalculatorPage.css";

const calculators = [
  {
    title: "EMI Calculators",
    icon: "fa-indian-rupee-sign",
    desc: "Calculate loan EMIs, total interest payable, and amortisation schedules.",
    tags: ["Home Loan", "Car Loan", "Personal"],
    color: "#2563eb",
    colorEnd: "#60a5fa",
    path: "/calculators/emi-calculate",
  },
  {
    title: "Mutual Fund Calculators",
    icon: "fa-chart-column",
    desc: "Estimate SIP returns, lump-sum growth, and XIRR on your MF investments.",
    tags: ["SIP", "Lump Sum", "XIRR"],
    color: "#0891b2",
    colorEnd: "#38bdf8",
    path: "/calculators/mutual-calculate",
  },
  {
    title: "Goal Planning Calculators",
    icon: "fa-bullseye",
    desc: "Plan for retirement, education, home purchase, and other life goals.",
    tags: ["Retirement", "Education", "Dream Home"],
    color: "#7c3aed",
    colorEnd: "#a78bfa",
    path: "/calculators/goal-calculate",
  },
  {
    title: "Capital Gains & Tax",
    icon: "fa-file-invoice-dollar",
    desc: "Compute STCG, LTCG tax liability and optimise your tax outgo.",
    tags: ["STCG", "LTCG", "Tax Saving"],
    color: "#10b981",
    colorEnd: "#6ee7b7",
    path: "/calculators/capital-tax-calculate",
  },
  {
    title: "Advanced MF Strategy Tools",
    icon: "fa-sliders",
    desc: "STP, SWP, step-up SIP and advanced portfolio rebalancing tools.",
    tags: ["STP", "SWP", "Step-Up"],
    color: "#f59e0b",
    colorEnd: "#fcd34d",
    path: "/calculators/advance-mutual-calculate",
  },
  {
    title: "Comparison & Inflation",
    icon: "fa-arrow-trend-up",
    desc: "Compare funds, beat inflation, and understand real purchasing power.",
    tags: ["Fund Compare", "Inflation", "Real Returns"],
    color: "#ef4444",
    colorEnd: "#fca5a5",
    path: "/calculators/comparison-inflation-calculate",
  },
  {
    title: "Inflation Calculator",
    icon: "fa-fire-flame-curved",
    desc: "See how inflation erodes money over time — future costs, real returns & inflation-adjusted goals.",
    tags: ["Future Cost", "Real Returns", "Goal Planning"],
    color: "#ef4444",
    colorEnd: "#fca5a5",
    path: "/calculators/inflation-calculate",
  },
  // {
  //   title: "Partner Calculators",
  //   icon: "fa-handshake",
  //   desc: "Exclusive tools and calculators available through our partner network.",
  //   tags: ["Exclusive", "Partners", "Special"],
  //   color: "#8b5cf6",
  //   colorEnd: "#c4b5fd",
  //   path: "/calculators/partner-calculate",
  // },
];

const stats = [
  { val: "20+", label: "Calculator Tools" },
  { val: "15K+", label: "Users Monthly" },
  { val: "100%", label: "Free to Use" },
];

function CalculatorPage() {
  const navigate = useNavigate();

  const handleOpen = (e, path) => {
    e.preventDefault();
    navigate(path);
    window.scrollTo(0, 0);
  };

  return (
    <div className="wcc-wrapper">
      <Navbar />

      {/* ── Hero Banner ── */}
      <section className="wcc-hero">
        <div className="wcc-hero-bg" />
        <div className="wcc-hero-dotgrid" />
        <div className="wcc-hero-glow wcc-glow-l" />
        <div className="wcc-hero-glow wcc-glow-r" />

        <div className="wcc-container wcc-hero-inner">
          <div className="wcc-hero-eyebrow">
            <i className="fas fa-calculator" /> Financial Calculators
          </div>
          <h1 className="wcc-hero-title">
            Make Smarter Decisions
            <br />
            <span className="wcc-gradient-text">with Precision Tools</span>
          </h1>
          <p className="wcc-hero-sub">
            7 powerful calculator suites covering every aspect of your financial
            life — from EMI planning to advanced MF strategy.
          </p>

          {/* Stats strip */}
          <div className="wcc-hero-stats">
            {stats.map((s) => (
              <div className="wcc-hero-stat" key={s.label}>
                <span className="wcc-stat-val">{s.val}</span>
                <span className="wcc-stat-lbl">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Calculator Grid ── */}
      <section className="wcc-grid-section">
        <div className="wcc-grid-bg" />
        <div className="wcc-blob wcc-blob-1" />
        <div className="wcc-blob wcc-blob-2" />

        <div className="wcc-container wcc-grid-inner">
          <div className="wcc-section-header">
            <div className="wcc-pill">
              <i className="fas fa-star" /> Our Tools
            </div>
            <h2 className="wcc-section-title">
              Choose Your <span className="wcc-gradient-text">Calculator</span>
            </h2>
            <p className="wcc-section-body">
              Each suite contains multiple calculators designed to give you
              instant, accurate answers so you can invest with confidence.
            </p>
          </div>

          <div className="wcc-grid">
            {calculators.map((calc, i) => (
              <Link
                key={i}
                to={calc.path}
                className="wcc-card-link"
                style={{ animationDelay: `${i * 60}ms` }}
                onClick={(e) => handleOpen(e, calc.path)}
              >
                <div className="wcc-card">
                  {/* Coloured top bar */}
                  <div
                    className="wcc-card-bar"
                    style={{
                      background: `linear-gradient(90deg, ${calc.color}, ${calc.colorEnd})`,
                    }}
                  />

                  {/* Icon */}
                  <div
                    className="wcc-card-icon"
                    style={{
                      background: `${calc.color}12`,
                      border: `1.5px solid ${calc.color}22`,
                    }}
                  >
                    <i
                      className={`fas ${calc.icon}`}
                      style={{ color: calc.color }}
                    />
                  </div>

                  <h3 className="wcc-card-title">{calc.title}</h3>
                  <p className="wcc-card-desc">{calc.desc}</p>

                  {/* Tags */}
                  <div className="wcc-card-tags">
                    {calc.tags.map((t) => (
                      <span
                        key={t}
                        className="wcc-tag"
                        style={{
                          color: calc.color,
                          background: `${calc.color}10`,
                          border: `1px solid ${calc.color}25`,
                        }}
                      >
                        {t}
                      </span>
                    ))}
                  </div>

                  {/* CTA row */}
                  <div
                    className="wcc-card-cta"
                    style={{ color: calc.color, borderColor: calc.color }}
                  >
                    Open Suite <i className="fas fa-arrow-right" />
                  </div>

                  {/* Hover glow */}
                  <div
                    className="wcc-card-glow"
                    style={{
                      background: `radial-gradient(ellipse 80% 40% at 50% 0%, ${calc.color}14, transparent 70%)`,
                    }}
                  />
                </div>
              </Link>
            ))}
          </div>

          {/* Bottom CTA banner */}
          <div className="wcc-cta-banner">
            <div className="wcc-cta-text">
              <h3>Need personalised advice beyond calculators?</h3>
              <p>
                Our advisors can translate your numbers into a real investment
                plan tailored to your goals.
              </p>
            </div>
            <Link
              to={getConsultationPath(WEB_LEAD_MODULES.CALC)}
              className="wcc-cta-btn"
              onClick={(e) => {
                e.preventDefault();
                navigate(getConsultationPath(WEB_LEAD_MODULES.CALC));
                window.scrollTo(0, 0);
              }}
            >
              Book Free Consultation <i className="fas fa-arrow-right" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default CalculatorPage;
